import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, RotateCcw, UserPlus, KeyRound } from "lucide-react";
import { ResetPasswordDialog } from "@/components/ResetPasswordDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const fieldCls = "bg-slate-900 border-slate-700 text-slate-100 h-10";
const empty = { name: "", email: "", password: "", role: "viewer" };

function AddUserForm({ onCreated }) {
  const [form, setForm] = useState(empty);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/users", form);
      onCreated(data);
      setForm(empty);
      toast.success("Utente creato");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-2 p-4 rounded-lg border border-slate-700/70 bg-slate-900/50">
      <Input required placeholder="Nome" value={form.name} onChange={set("name")} className={fieldCls} data-testid="add-user-name-input" />
      <Input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={fieldCls} data-testid="add-user-email-input" />
      <Input required type="password" minLength={8} placeholder="Password (min 8)" value={form.password} onChange={set("password")} className={fieldCls} data-testid="add-user-password-input" />
      <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
        <SelectTrigger className={fieldCls} data-testid="add-user-role-select"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
          <SelectItem value="viewer" data-testid="add-user-role-viewer">Utente</SelectItem>
          <SelectItem value="admin" data-testid="add-user-role-admin">Admin</SelectItem>
        </SelectContent>
      </Select>
      <Button className="col-span-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-user-submit-button">
        <UserPlus className="w-4 h-4 mr-1.5" /> Crea utente
      </Button>
    </form>
  );
}

export const UsersDialog = ({ open, onOpenChange, currentUserId }) => {
  const [users, setUsers] = useState([]);
  const [pwdTarget, setPwdTarget] = useState(null);

  useEffect(() => {
    if (open) api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [open]);

  const remove = async (u) => {
    if (!window.confirm(`Eliminare ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((l) => l.filter((x) => x.id !== u.id));
      toast.success("Utente eliminato");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const resetOtp = async (u) => {
    try {
      await api.post(`/users/${u.id}/reset-otp`);
      setUsers((l) => l.map((x) => (x.id === u.id ? { ...x, totp_enabled: false } : x)));
      toast.success(`OTP di ${u.name} reimpostato: al prossimo accesso vedrà un nuovo QR`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-[#111827] border-slate-700 text-slate-100 sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="users-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Gestione utenti</DialogTitle></DialogHeader>
        <AddUserForm onCreated={(u) => setUsers((l) => [...l, u])} />
        <ul className="divide-y divide-slate-800" data-testid="users-list">
          {users.map((u) => (
            <li key={u.id} className="flex items-center gap-3 py-3" data-testid={`user-row-${u.email}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-100 truncate">{u.name}
                  <span className="ml-2 font-mono text-[10px] uppercase text-slate-500">{u.role === "admin" ? "admin" : "utente"}</span>
                </p>
                <p className="font-mono text-xs text-slate-400 truncate">{u.email}</p>
              </div>
              <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full border ${u.totp_enabled ? "text-emerald-400 border-emerald-500/30" : "text-amber-400 border-amber-500/30"}`}>
                {u.totp_enabled ? "OTP attivo" : "OTP da attivare"}
              </span>
              <Button size="icon" variant="ghost" title="Imposta nuova password" disabled={u.id === currentUserId}
                onClick={() => setPwdTarget(u)} data-testid={`reset-password-button-${u.id}`}
                className="text-slate-400 hover:text-white hover:bg-slate-800"><KeyRound className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" title="Reimposta OTP" onClick={() => resetOtp(u)} data-testid={`reset-otp-button-${u.id}`}
                className="text-slate-400 hover:text-white hover:bg-slate-800"><RotateCcw className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" title="Elimina" disabled={u.id === currentUserId} onClick={() => remove(u)} data-testid={`delete-user-button-${u.id}`}
                className="text-slate-400 hover:text-rose-400 hover:bg-slate-800"><Trash2 className="w-4 h-4" /></Button>
            </li>
          ))}
        </ul>
        <ResetPasswordDialog target={pwdTarget} onClose={() => setPwdTarget(null)} />
      </DialogContent>
    </Dialog>
  );
};
